/**
 * The hand-rolled MSAL integration (deliberately no @azure/msal-react — see
 * the msal-browser dependency evaluation's scope note). One PublicClient
 * instance, redirect flow with PKCE (the SPA platform's construction), and
 * sessionStorage token cache: tokens die with the tab — the conservative
 * default recorded as an evaluation condition.
 */
import {
  CacheLookupPolicy,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from '@azure/msal-browser'
import { authConfig } from './config'

export const msal = new PublicClientApplication({
  auth: {
    clientId: authConfig.clientId,
    authority: authConfig.authority,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: { cacheLocation: 'sessionStorage' },
})

let initialization: Promise<void> | undefined

/** Idempotent; safe to await from any route's beforeLoad. */
export function ensureInitialized(): Promise<void> {
  initialization ??= (async () => {
    await msal.initialize()
    // Completes the redirect round-trip when the IdP sends the user back.
    const result = await msal.handleRedirectPromise()
    if (result?.account) msal.setActiveAccount(result.account)
  })()
  return initialization
}

/**
 * Development-only rendering stub, so the app can be LOOKED at without an
 * interactive sign-in (see `tools/preview`). Wave 9 spent a session finding
 * defects by reading output and could not read this one, because every page is
 * behind a redirect.
 *
 * Safe BY CONSTRUCTION rather than by convention: `import.meta.env.DEV` is
 * `false` in every production build, so this collapses to a constant `false`
 * and the branch is removed by the bundler. Setting VITE_PREVIEW against a
 * built artifact does nothing at all.
 *
 * It stubs RENDERING, never authorization. Every gate this app has lives at
 * the API and is proven there by tests that call it with the wrong identity;
 * nothing here can grant anybody anything.
 */
const PREVIEW = import.meta.env.DEV && Boolean(import.meta.env.VITE_PREVIEW)

export function activeAccount(): AccountInfo | null {
  if (PREVIEW) {
    return { homeAccountId: 'preview', localAccountId: 'preview', environment: 'preview',
      tenantId: 'preview', username: 'preview@example.test', name: 'Preview' } as AccountInfo
  }

  const active = msal.getActiveAccount()
  if (active) return active
  const first = msal.getAllAccounts()[0] ?? null
  if (first) msal.setActiveAccount(first)
  return first
}

export function signIn(): Promise<void> {
  return msal.loginRedirect({ scopes: [authConfig.apiScope] })
}

/**
 * Access token for the JEDI API: silent first, full redirect only when the
 * IdP genuinely requires interaction (expired session, revoked consent).
 */
export async function acquireApiToken(): Promise<string> {
  if (PREVIEW) return 'preview-token'

  await ensureInitialized()
  const account = activeAccount()
  if (!account) throw new Error('Not signed in')
  try {
    const result = await msal.acquireTokenSilent({ scopes: [authConfig.apiScope], account })
    return result.accessToken
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({ scopes: [authConfig.apiScope], account })
      throw new Error('Redirecting to sign-in')
    }
    throw error
  }
}

/**
 * A token for the people picker's directory search (ADR-0045), or null when
 * this environment has not been granted one, in which case the picker falls
 * back to typing the address.
 *
 * SILENT ONLY, and deliberately unlike `acquireApiToken` above. That one
 * redirects when the identity provider asks for interaction; this one must not,
 * because in this tenant users cannot consent to anything, so the page a
 * redirect would land on is a dead end for everybody. It tries the cache and
 * the refresh token and skips the hidden-frame renewal, so a missing consent
 * answers in one round trip rather than after that frame's timeout.
 */
export async function acquireDirectoryToken(): Promise<string | null> {
  if (PREVIEW) return 'preview-token'

  await ensureInitialized()
  const account = activeAccount()
  if (!account) return null
  try {
    const result = await msal.acquireTokenSilent({
      scopes: [authConfig.directoryScope],
      account,
      cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
    })
    return result.accessToken
  } catch {
    return null
  }
}
